import React, {
  forwardRef,
  ReactElement,
  ReactNode,
  useCallback,
  useState,
} from 'react';

import PropTypes from 'prop-types';

import AnimateHeight from '@adsk/alloy-react-animate-height';
import Checkbox, { CheckboxState } from '@adsk/alloy-react-checkbox';
import { useDeprecate } from '@adsk/alloy-react-helpers';
import HighlightText from '@adsk/alloy-react-highlight-text';
import { RenderIconType } from '@adsk/alloy-react-icon';
import { useTheme } from '@adsk/alloy-react-styles';
import { stylePropType, StylableComponent } from '@adsk/alloy-react-theme';

import ExpandButton from './layout/ExpandButton';
import Indent from './layout/Indent';
import Label from './layout/Label';
import { IdType, InternalDataNode, RenderElementType } from './types';

const defaultRenderWrapper = (props: {
  id: string;
  show: boolean;
  children: ReactElement;
  'data-testid': string;
}) => {
  const { show, ...rest } = props;
  return <AnimateHeight isOpen={show} {...rest} />;
};

const elementProps = {
  style: {
    /* total width must equal indent unit of 30 */
    margin: `0 3px`,
    minWidth: 24,
  },
};

const BASE_PADDING = { padding: '0 10px' };

export type TreeNodeProps = Omit<
  StylableComponent<HTMLLIElement>,
  'id' | 'onSelect'
> &
  Pick<InternalDataNode, 'id'> & {
    label: ReactNode;
    show: boolean;
    isExpanded?: boolean;
    depth?: number;
    parentDepth?: number;
    isLastAtDepth?: boolean;
    /** @deprecated use renderLeftElement*/
    renderLeftIcon?: RenderIconType;
    renderLeftElement?: RenderElementType;
    renderRightElement?: RenderElementType;
    isDisabled?: boolean;
    isSelected?: CheckboxState;
    isExpandable?: boolean;
    isSingleSelectable?: boolean;
    isMultiSelectable?: boolean;
    isHighlighted?: boolean;
    guidelines?: boolean;
    onExpand?: (p: {
      isExpanded: boolean;
      id: IdType;
      recurse: boolean;
    }) => void;
    onSelect?: (p: { isSelected: CheckboxState; id: IdType }) => void;
    renderWrapper?: (p: {
      id: string;
      show: boolean;
      children: ReactElement;
      'data-testid': string;
    }) => ReactElement;
    children?: ReactNode;
    /** Filter value for highlighting search text */
    filterValue?: string;
    /** Enable highlighting of search text */
    enableSearchHighlight?: boolean;
  };

const TreeNode = forwardRef<HTMLLIElement, TreeNodeProps>(
  (
    {
      id,
      depth = 0,
      parentDepth,
      isLastAtDepth,
      label,
      show,
      renderLeftIcon,
      renderLeftElement,
      renderRightElement,
      isDisabled = false,
      isExpandable = false,
      isSingleSelectable = false,
      isMultiSelectable = false,
      isExpanded = false,
      isSelected = false,
      isHighlighted = false,
      guidelines = false,
      onExpand,
      onSelect,
      renderWrapper = defaultRenderWrapper,
      children,
      onMouseDown,
      onMouseUp,
      onMouseLeave,
      style,
      filterValue,
      enableSearchHighlight,
      ...props
    },
    ref,
  ) => {
    useDeprecate(
      !!renderLeftIcon,
      'TreeNode',
      'renderLeftIcon',
      'renderLeftElement',
    );

    const rowClick = useCallback<React.MouseEventHandler<HTMLLIElement>>(
      (event) => {
        if (isDisabled) return undefined;
        if (isSingleSelectable || isMultiSelectable) {
          return onSelect
            ? onSelect({ isSelected: !isSelected, id })
            : undefined;
        }
        if (isExpandable) {
          return onExpand
            ? onExpand({
                isExpanded: !isExpanded,
                id,
                recurse: event.shiftKey,
              })
            : undefined;
        }
        return undefined;
      },
      [
        id,
        onSelect,
        onExpand,
        isSingleSelectable,
        isMultiSelectable,
        isExpandable,
        isExpanded,
        isSelected,
        isDisabled,
      ],
    );

    const [isPressed, setIsPressed] = useState(false);
    const onMouseDownHandler: React.MouseEventHandler<HTMLLIElement> =
      useCallback(
        (e) => {
          setIsPressed(true);
          onMouseDown?.(e);
        },
        [onMouseDown],
      );
    const onMouseUpHandler: React.MouseEventHandler<HTMLLIElement> =
      useCallback(
        (e) => {
          setIsPressed(false);
          onMouseUp?.(e);
        },
        [onMouseUp],
      );

    const onMouseLeaveHandler: React.MouseEventHandler<HTMLLIElement> =
      useCallback(
        (e) => {
          setIsPressed(false);
          onMouseLeave?.(e);
        },
        [onMouseLeave],
      );

    const { tokens } = useTheme();
    const nodeTokens = tokens?.component.tree?.node;

    const isRowClickable = !isDisabled && !!rowClick;
    const isSelectedRow = isSingleSelectable && !!isSelected;

    return renderWrapper({
      show,
      id: id.toString(),
      'data-testid': `TreeNodeAnimateHeight_${id}`,
      children: (
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
        <li
          ref={ref}
          onClick={rowClick}
          onMouseDown={onMouseDownHandler}
          onMouseUp={onMouseUpHandler}
          onMouseLeave={onMouseLeaveHandler}
          css={[
            nodeTokens?._,
            BASE_PADDING,
            isRowClickable
              ? nodeTokens?.rowClick?.true
              : isDisabled
                ? nodeTokens?.rowClick?.false
                : undefined,
            isHighlighted && nodeTokens?.state?.hover,
            isPressed && !isDisabled && nodeTokens?.state?.pressed,
            isSelectedRow && nodeTokens?.state?.selected,
            isDisabled && nodeTokens?.state?.disabled,
            style,
          ]}
          {...props}
        >
          <Indent
            depth={depth}
            guidelines={guidelines}
            isExpandable={isExpandable}
            isLastAtDepth={isLastAtDepth}
            nodeId={id.toString()}
          />
          {children || (
            <>
              {isExpandable && (
                <ExpandButton
                  style={{
                    /* total width must equal indent unit of 30 */
                    margin: `0 3px`,
                  }}
                  isExpanded={isExpanded}
                  onExpand={(changes) => onExpand?.({ ...changes, id })}
                />
              )}
              {isMultiSelectable && (
                <Checkbox
                  id={`${id}-checkbox`}
                  style={{ margin: `0 7px` }}
                  data-testid="Checkbox"
                  disabled={isDisabled}
                  checked={isSelected}
                  onChange={(val) =>
                    onSelect?.({
                      id,
                      isSelected: val,
                    })
                  }
                  onClick={(e) => e.stopPropagation()}
                />
              )}
              {(renderLeftElement || renderLeftIcon)?.(elementProps)}
              <Label>
                {enableSearchHighlight &&
                filterValue &&
                filterValue.trim() &&
                typeof label === 'string' ? (
                  <HighlightText text={label} matched={filterValue.trim()} />
                ) : (
                  label
                )}
              </Label>
              {renderRightElement?.(elementProps)}
            </>
          )}
        </li>
      ),
    });
  },
);

TreeNode.displayName = 'TreeNode';

TreeNode.propTypes = {
  /** Unique identifier for the current node */
  id: PropTypes.string.isRequired,
  /** Determines whether or not to visibly render the node */
  show: PropTypes.bool.isRequired,
  /** The nesting level for this node. Ex. if the node has a parent and grandparent the
   * depth would be 2.
   */
  depth: PropTypes.number,
  /** Text to be rendered on the node */
  label: PropTypes.string.isRequired,
  /** Deprecated: The icon to render on the left side of the label */
  renderLeftIcon: PropTypes.func,
  /** Display a React element on left side of the label */
  renderLeftElement: PropTypes.func,
  /** Display a React element on right side of the label */
  renderRightElement: PropTypes.func,
  /** Determines if the node is currently disabled, if applicable */
  isDisabled: PropTypes.bool,
  /** Determines if the node is currently expanded, if applicable */
  isExpanded: PropTypes.bool,
  /** Determines if the node is currently selected, if applicable */
  isSelected: PropTypes.oneOf([true, false, 'indeterminate']),
  /** Handler for expanding the current node, in order to show its children */
  onExpand: PropTypes.func,
  /** Handler for selecting the current node */
  onSelect: PropTypes.func,
  /** Render the parent element which wraps the tree node markup, defaults to an animate
   * height element
   */
  renderWrapper: PropTypes.func,
  /** Handler function for mouse down event */
  onMouseDown: PropTypes.func,
  /** Handler function for mouse up event */
  onMouseUp: PropTypes.func,
  /** Handler function for mouse leave event */
  onMouseLeave: PropTypes.func,
  /** Any child elements to override the default, exluding the indentation */
  children: PropTypes.any,
  /** style object to add to the wrapper of the node */
  style: stylePropType,
  /** Determines if the node is expandable, if applicable */
  isExpandable: PropTypes.bool,
  /** Determines if the node can be selected individually, if applicable */
  isSingleSelectable: PropTypes.bool,
  /** Determines if then node can be selected as a part of a group, if applicable */
  isMultiSelectable: PropTypes.bool,
  /** Determines if the node can be highlighted, if applicable */
  isHighlighted: PropTypes.bool,
};

export default TreeNode;
